"use client";

import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useRef, useState } from "react";

import { CareFormStatus, type TaskStats } from "@/care-forms/models";
import {
	VerbalConfirmationInput,
	type VerbalConfirmationRecording,
} from "@/caregiver-app/components/verbal-confirmation-input";
import { IvrInstructionsModal } from "@/components/ivr-instructions-modal";
import { useIsMobile } from "@/common/hooks/use-mobile";
import {
	combineDateAndTime,
	formatMinutesDuration,
} from "@/common/utils/time-utils";
import { trpc, useTRPC } from "@/common/utils/trpc";
import { LocalizedTimeText } from "@/components/ui/localized-time-text";
import { SignatureInput } from "@/components/ui/signature-input";
import {
	ActionIcon,
	Alert,
	Box,
	Button,
	Card,
	Checkbox,
	Container,
	Divider,
	Flex,
	Grid,
	Group,
	Input,
	LoadingOverlay,
	Modal,
	NumberInput,
	Progress,
	Radio,
	Select,
	SimpleGrid,
	Stack,
	Text,
	Textarea,
	Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import {
	IconAlertCircle,
	IconAlertTriangle,
	IconArrowLeft,
	IconCheck,
	IconMicrophone,
	IconPlus,
	IconReportMedical,
	IconTrash,
} from "@tabler/icons-react";
import { onlineManager, useMutation } from "@tanstack/react-query";
import {
	Link,
	Navigate,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router";

import {
	caregiverAppClientSignatureDialogDescription,
	caregiverAppSignatureDisclaimer,
} from "@/caregiver-app/components/constants";
import { useGetCheckInOutData } from "@/caregiver-app/components/useGetCheckInOutData";

import {
	useCheckInOutOfflineCache,
	useGetCareSheetCheckOutDetails,
} from "@/caregiver-app/components/hooks";
import { navigateBack } from "@/common/utils/navigateBack";
import { ROUTES } from "@/setup/frontend/routes";

import { isNetworkError } from "@/common/utils/is-network-error";
import { randomUuidCompat } from "@/common/utils/randomUuidCompat";
import { useLogger } from "@/logging/client/LoggerContext";
import { useInvalidateCaregiverAppShiftsQuery } from "@/setup/frontend/api/apiHooks";
import { useGetGpsLocation } from "../components/useGetGpsLocation";
import { useOfflineVerbalConfirmationCache } from "../hooks/useOfflineVerbalConfirmationCache";
import { queryMicrophonePermission } from "../utils/microphone-permission";
import { IvrInstructionsButton } from "../../components/IvrInstructionsButton";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);

// Client unable to sign reason options
const CLIENT_UNABLE_TO_SIGN_REASONS = [
	"Physical inability (paralysis, weakness, blindness, injury)",
	"Cognitive inability (dementia, confusion, impairment)",
	"Client hospitalized",
	"Medical emergency or distress",
];

type TransportationEntry = {
	startTime: string;
	endTime: string;
	totalMiles: number;
	destinationAndPurpose: string;
	errandsTime: number;
	activitiesTime: number;
	wasClientWithYou: boolean;
};

type FormStep = "timeEntry" | "tasks" | "transportation" | "comment" | "review";
type ClientVerificationMethod =
	| "signature"
	| "voiceRecording"
	| "unableToVerifyReason";

// An in-app recording that is already uploaded and stored on the visit.
type SavedVerbalConfirmationRecording = { id: string; contentType: string };

const maxTransportationEntries = 3;

function format24HourTime(time: string | undefined) {
	if (!time) {
		return "";
	}
	const [hours, minutes] = time.split(":");
	const now = dayjs();
	const formattedTime = now
		.set("hour", Number.parseInt(hours))
		.set("minute", Number.parseInt(minutes))
		.format("h:mm A");
	return formattedTime;
}

function getCurrentTime() {
	return dayjs().format("HH:mm");
}

// Large time differences are often caused by simple data entry mistakes (entering PM
// instead of AM, or picking the wrong date) rather than an actual schedule deviation.
// A difference close to any multiple of 12 hours is consistent with either mistake
// (or both at once, e.g. wrong date AND flipped AM/PM), so we suggest checking both
// rather than guessing a single cause.
function shouldShowTimeDiffHint(diffMinutes: number): boolean {
	const diffHours = diffMinutes / 60;
	const nearestTwelveHourMultiple = Math.round(diffHours / 12) * 12;
	return (
		nearestTwelveHourMultiple > 0 &&
		Math.abs(diffHours - nearestTwelveHourMultiple) <= 0.5
	);
}

// Helper function to determine if a task is required for today based on scheduling and frequency
function isTaskRequiredForToday(
	task: {
		category: string;
		name: string;
	},
	checkoutDetails: {
		shift: {
			start_timestamp: string;
		};
		taskStats: {
			category: string;
			name: string;
			allowedDays?: number[] | null | undefined;
			occurrencesFrequency?: "perDay" | "perWeek" | null | undefined;
			minutesLimit?: number | null | undefined;
			minutesFrequency?: "perDay" | "perWeek" | null | undefined;
			occurrencesLimit?: number | null | undefined;
		}[];
	},
): boolean {
	const currentDayOfWeek = dayjs(checkoutDetails.shift.start_timestamp).day();
	const taskStat = checkoutDetails.taskStats.find(
		(stat) => stat.category === task.category && stat.name === task.name,
	);

	// Required if scheduled for today or has daily frequency
	return (
		(taskStat?.allowedDays?.length &&
			taskStat.allowedDays.includes(currentDayOfWeek)) ||
		(taskStat?.occurrencesFrequency === "perDay" &&
			!taskStat?.allowedDays?.length &&
			taskStat?.occurrencesLimit !== undefined &&
			taskStat?.occurrencesLimit !== null &&
			taskStat?.occurrencesLimit > 0)
	);
}

function simulateTaskStats(
	existingStats: TaskStats,
	newTask: { timeMinutes?: number },
	timezone: string,
	currentTimestamp: Date | string,
	isExistingTask = false,
	previousTimeMinutes = 0,
): TaskStats {
	const dayOfWeek = dayjs(currentTimestamp).tz(timezone).day();

	// For existing tasks, subtract the previous minutes from the total before adding new minutes
	const baseTotalMinutes = isExistingTask
		? existingStats.totalMinutes - previousTimeMinutes
		: existingStats.totalMinutes;

	// Calculate adjusted minutes only if timeMinutes is provided
	const adjustedTotalMinutes =
		newTask.timeMinutes !== undefined
			? baseTotalMinutes + newTask.timeMinutes
			: baseTotalMinutes;

	const adjustedTotalOccurrences =
		existingStats.totalOccurrences + (isExistingTask ? 0 : 1);

	return {
		...existingStats,
		totalMinutes: adjustedTotalMinutes,
		totalOccurrences: adjustedTotalOccurrences,
		isOverMinutes:
			existingStats.minutesLimit !== undefined &&
			existingStats.minutesLimit !== null &&
			(existingStats.minutesFrequency === "perDay"
				? newTask.timeMinutes !== undefined &&
					newTask.timeMinutes > existingStats.minutesLimit
				: adjustedTotalMinutes > existingStats.minutesLimit),
		isOverOccurrences:
			existingStats.occurrencesLimit !== undefined &&
			existingStats.occurrencesLimit !== null &&
			adjustedTotalOccurrences >
				(existingStats.occurrencesFrequency === "perDay"
					? existingStats.occurrencesLimit * 7
					: existingStats.occurrencesLimit),
		isWrongDay:
			Boolean(existingStats.allowedDays?.length) &&
			Boolean(!existingStats.allowedDays?.includes(dayOfWeek)),
	};
}

type TaskWithWarnings = {
	category: string;
	name: string;
	notes?: string;
	timeMinutes?: number;
	simulatedStats?: TaskStats;
};

// This acts as a simple mechanism for slowly rolling out the survey feature.
// It also staggers the survey cadence so we don't get 1000s of survey submissions at once.
const SURVEY_PROBABILITY = 0.25;
const isRandomSurveyEnabled = Math.random() <= SURVEY_PROBABILITY;

export default function CareSheetCheckoutPage() {
	const { care_sheet_id, shift_id } = useParams<{
		care_sheet_id: string;
		shift_id: string;
	}>();
	const careSheetId = Number(care_sheet_id);
	const shiftId = Number(shift_id);
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const t = useTRPC();
	const [searchParams] = useSearchParams();
	const microphoneSkipped = searchParams.get("microphoneSkipped") === "true";
	const [currentStep, setCurrentStep] = useState<FormStep>("timeEntry");
	const [hasFormChanges, setHasFormChanges] = useState(false);
	const [isSurveyHidden, setIsSurveyHidden] = useState(false);

	function toggleSurvey() {
		setIsSurveyHidden((current) => {
			return !current;
		});
	}

	const {
		data: checkoutDetails,
		isLoading: checkOutDetailInitialLoading,
		error: checkOutDetailLoadError,
	} = useGetCareSheetCheckOutDetails(shiftId);

	const [completedTasks, setCompletedTasks] = useState<TaskWithWarnings[]>([]);
	const [initialCompletedTasks, setInitialCompletedTasks] = useState<
		TaskWithWarnings[]
	>([]);
	const [clientInitials, setClientInitials] = useState<string | null>(null);
	const [clientSignature, setClientSignature] = useState<string | null>(null);
	const [caregiverSignature, setCaregiverSignature] = useState<string | null>(
		null,
	);
	const [caregiverInitials, setCaregiverInitials] = useState<string | null>(
		null,
	);
	const [clientUnableToSignReason, setClientUnableToSignReason] = useState<
		string | null
	>(null);
	const [
		clientVerbalConfirmationRecording,
		setClientVerbalConfirmationRecording,
	] = useState<VerbalConfirmationRecording | null>(null);
	const [
		savedClientVerbalConfirmationRecording,
		setSavedClientVerbalConfirmationRecording,
	] = useState<SavedVerbalConfirmationRecording | null>(null);
	const [unableToSignModalOpened, setUnableToSignModalOpened] = useState(false);
	const [clientVerificationMethod, setClientVerificationMethod] =
		useState<ClientVerificationMethod | null>(null);
	const [timeDifferenceModalOpened, setTimeDifferenceModalOpened] =
		useState(false);

	function clearClientVerification() {
		setClientSignature(null);
		setClientInitials(null);
		setClientUnableToSignReason(null);
		setClientVerbalConfirmationRecording(null);
		setSavedClientVerbalConfirmationRecording(null);
	}

	function clearSignaturesAndConfirmations() {
		clearClientVerification();
		setCaregiverSignature(null);
		setCaregiverInitials(null);
	}

	function selectClientVerificationMethod(value: ClientVerificationMethod) {
		if (value === clientVerificationMethod) {
			return;
		}
		setClientVerificationMethod(value);
	}
	const [microphonePermissionGranted, setMicrophonePermissionGranted] =
		useState<boolean | null>(null);
	useEffect(() => {
		queryMicrophonePermission().then((status) => {
			setMicrophonePermissionGranted(status === "granted");
		});
	}, []);
	const [includeTransportation, setIncludeTransportation] = useState<
		boolean | null
	>(null);
	const [initialIncludeTransportation, setInitialIncludeTransportation] =
		useState<boolean | null>(null);
	const [transportationData, setTransportationData] = useState<
		Partial<TransportationEntry>[]
	>([]);
	const [initialTransportationData, setInitialTransportationData] = useState<
		Partial<TransportationEntry>[]
	>([]);
	const [timeWarnings, setTimeWarnings] = useState<{
		startBeforeScheduled?: boolean[];
		endAfterScheduled?: boolean[];
		startAfterEnd?: boolean[];
	}>({});
	const [checkIn, setCheckIn] = useState<string | null>(null);
	const [checkOut, setCheckOut] = useState<string | null>(null);
	const [checkInDateValue, setCheckInDateValue] = useState<string | null>(null);
	const [checkOutDateValue, setCheckOutDateValue] = useState<string | null>(
		null,
	);
	const [originalCheckIn, setOriginalCheckIn] = useState<string | null>(null);
	const [_, setOriginalCheckOut] = useState<string | null>(null);
	const [caregiverComment, setCaregiverComment] = useState<string>("");
	const [experienceRating, setExperienceRating] = useState<number | null>(null);
	const invalidateShiftsQuery = useInvalidateCaregiverAppShiftsQuery();
	const logger = useLogger();
	const preemptiveGpsPosition = useRef<GeolocationPosition | null>(null);

	const initializedStateRef = useRef(false);

	const getCheckInOutData = useGetCheckInOutData();
	const { getGpsLocation } = useGetGpsLocation();
	const innerScrollRef = useRef<HTMLDivElement>(null);

	const utils = trpc.useUtils();

	const checkInOutOfflineCache = useCheckInOutOfflineCache();
	const verbalConfirmationCache = useOfflineVerbalConfirmationCache();
	const getClientVerbalConfirmationUploadUrl = useMutation(
		t.shifts.getClientVerbalConfirmationUploadUrl.mutationOptions(),
	);
	const remoteSaveCareSheetCheckOut =
		trpc.careSheets.saveCareSheetCheckInOut.useMutation({
			onSuccess: (data, vars) => {
				(vars.checkInOut.checkOut
					? checkInOutOfflineCache.removeCheckOut(shiftId)
					: checkInOutOfflineCache.removeCheckIn(shiftId)
				).finally(() => {
					// update offline data to persist optimistic update until all data is refreshed from server
					checkInOutOfflineCache.updateOfflineCaregiverAppShiftsCache(
						shiftId,
						vars.checkInOut.checkIn,
						vars.checkInOut.checkOut,
					);
					checkInOutOfflineCache.updateOfflineCheckoutDetailsCache(
						shiftId,
						vars,
					);
					utils.careSheets.listMyCareSheets.invalidate();
					utils.careSheets.getCareSheetCheckOutDetails.invalidate({
						shift_id: shiftId,
					});
					invalidateShiftsQuery();
				});

				notifications.show({
					title: "Success",
					message: vars.checkInOut.checkOut
						? "Check out Successful"
						: "Check in Successful",
					color: "green",
				});
				if (
					data.status === CareFormStatus.caregiverReview ||
					data.status === CareFormStatus.clientReview
				) {
					navigate(ROUTES.CAREGIVER_APP.CARE_SHEETS.REVIEW(careSheetId), {
						replace: true,
					});
				} else {
					navigate(
						ROUTES.CAREGIVER_APP.SHIFT_DEEPLINK(
							checkoutDetails?.shift.start_timestamp ?? "",
						),
						{
							replace: true,
						},
					);
				}
			},
			onError: (error, vars) => {
				logger.error(
					`Error saving care sheet check-in/out. Error: ${error instanceof Error ? error.message : error}`,
				);
				if (isNetworkError(error)) {
					notifications.show({
						title: "Saved Offline",
						message: vars.checkInOut.checkOut
							? "Your check out will be submitted when you are back online."
							: "Your check in will be submitted when you are back online.",
						color: "blue",
						id: "care-sheet-check-in-out-offline",
					});
					navigate(
						ROUTES.CAREGIVER_APP.SHIFT_DEEPLINK(
							checkoutDetails?.shift.start_timestamp ?? "",
						),
						{
							replace: true,
						},
					);
				} else {
					if (vars.checkInOut.checkOut) {
						checkInOutOfflineCache.removeCheckOut(shiftId);
					} else {
						checkInOutOfflineCache.removeCheckIn(shiftId);
					}
					notifications.show({
						title: "Error",
						message: error.message,
						color: "red",
					});
				}
			},
			// always execute the mutation even if the network is offline
			// This prevents the mutation from being 'paused' and executed later and conflicting with the offline sync mechanism.
			networkMode: "always",
		});

	const localSaveCareSheetCheckOut = useMutation({
		mutationFn: async () => {
			if (!checkoutDetails) {
				throw new Error("Checkout details not found");
			}

			const checkInOutData = await getCheckInOutData({
				shift: {
					id: shiftId,
					start_timestamp: checkoutDetails.shift.start_timestamp,
					end_timestamp: checkoutDetails.shift.end_timestamp,
					evv_consent_granted: checkoutDetails.evv_consent_granted,
				},
				checkInTime: checkIn || undefined,
				checkOutTime: checkOut || undefined,
				checkInDate: checkInDateValue || undefined,
				checkOutDate: checkOutDateValue || undefined,
				preemptiveGpsPosition: preemptiveGpsPosition.current,
			});

			// An already uploaded recording is resubmitted as is when only the comment was
			// edited. Editing anything else clears it and requires a new recording.
			const submittedVerbalConfirmationRecording = checkInOutData.checkOut
				? clientVerbalConfirmationRecording
					? {
							id: randomUuidCompat(),
							contentType: clientVerbalConfirmationRecording.blob.type,
						}
					: savedClientVerbalConfirmationRecording
				: null;
			const clientVerbalConfirmationRecordingId =
				submittedVerbalConfirmationRecording?.id;

			// Upload verbal confirmation recording to S3 if present
			const variables = {
				shift_id: shiftId,
				completedTasks,
				survey: experienceRating ? { experienceRating } : undefined,
				clientInitials: checkoutDetails.clientInitialsRequired
					? clientInitials || undefined
					: undefined,
				clientSignature: checkoutDetails.clientSignatureRequired
					? clientSignature || undefined
					: undefined,
				caregiverSignature: checkoutDetails.caregiverSignatureRequired
					? caregiverSignature || undefined
					: undefined,
				caregiverInitials: checkoutDetails.caregiverInitialsRequired
					? caregiverInitials || undefined
					: undefined,
				caregiverComment: caregiverComment?.trim() || undefined,
				transportationData: includeTransportation
					? (transportationData as TransportationEntry[])
					: undefined,
				clientUnableToSignReason: clientUnableToSignReason || undefined,
				clientVerbalConfirmationRecordingId:
					clientVerbalConfirmationRecordingId,
				clientVerbalConfirmationRecordingContentType:
					submittedVerbalConfirmationRecording?.contentType,
				checkInOut: {
					...checkInOutData,
				},
			};

			if (variables.checkInOut.checkOut) {
				await checkInOutOfflineCache
					.setCheckOut(shiftId, variables)
					.catch((error) => {
						logger.error(
							`Error setting check-out for shift ${shiftId}. Error: ${error instanceof Error ? error.message : error}`,
						);
						// log but proceed if offline save fails
					});
			} else {
				await checkInOutOfflineCache
					.setCheckIn(shiftId, variables)
					.catch((error) => {
						logger.error(
							`Error setting check-in for shift ${shiftId}. Error: ${error instanceof Error ? error.message : error}`,
						);
					});
			}

			function deferRemoteSave() {
				// Save offline when timeout occurs
				notifications.show({
					title: "Saved Offline",
					message: variables.checkInOut.checkOut
						? "Your check out will be submitted when you are back online."
						: "Your check in will be submitted when you are back online.",
					color: "blue",
					id: "care-sheet-check-in-out-offline",
				});
				navigate(
					ROUTES.CAREGIVER_APP.SHIFT_DEEPLINK(
						checkoutDetails?.shift.start_timestamp ?? "",
					),
					{
						replace: true,
					},
				);
			}

			if (
				clientVerbalConfirmationRecording &&
				clientVerbalConfirmationRecordingId
			) {
				logger.info(
					`verbal_confirmation_checkout_started shiftId=${shiftId} recordingId=${clientVerbalConfirmationRecordingId} sizeBytes=${clientVerbalConfirmationRecording.blob.size} contentType=${clientVerbalConfirmationRecording.blob.type} durationSeconds=${clientVerbalConfirmationRecording.durationSeconds} online=${onlineManager.isOnline()}`,
				);
				try {
					await verbalConfirmationCache.set(
						shiftId,
						clientVerbalConfirmationRecording.blob,
						clientVerbalConfirmationRecordingId,
					);
					logger.info(
						`verbal_confirmation_cached shiftId=${shiftId} recordingId=${clientVerbalConfirmationRecordingId}`,
					);
				} catch (cacheError) {
					logger.warn(
						`Failed to cache verbal confirmation for shift ${shiftId}: ${cacheError instanceof Error ? cacheError.message : cacheError}`,
					);
				}
				try {
					if (onlineManager.isOnline()) {
						logger.info(
							`verbal_confirmation_upload_started shiftId=${shiftId} recordingId=${clientVerbalConfirmationRecordingId}`,
						);
						const timeoutPromise = new Promise((_, reject) => {
							setTimeout(() => {
								reject(new Error("Verbal confirmation upload timed out"));
							}, 10_000);
						});
						await Promise.race([
							getClientVerbalConfirmationUploadUrl
								.mutateAsync({
									shift_id: shiftId,
									recording_id: clientVerbalConfirmationRecordingId,
									file_size_bytes: clientVerbalConfirmationRecording.blob.size,
									content_type: clientVerbalConfirmationRecording.blob.type,
								})
								.then(async ({ uploadUrl }) => {
									const uploadResponse = await fetch(uploadUrl, {
										method: "PUT",
										body: clientVerbalConfirmationRecording.blob,
										headers: {
											"Content-Type":
												clientVerbalConfirmationRecording.blob.type,
										},
									});
									if (!uploadResponse.ok) {
										throw new Error(
											`S3 upload failed with status ${uploadResponse.status}`,
										);
									}
								}),
							timeoutPromise,
						]);
						logger.info(
							`verbal_confirmation_upload_succeeded shiftId=${shiftId} recordingId=${clientVerbalConfirmationRecordingId}`,
						);
						await verbalConfirmationCache.remove(shiftId);
						logger.info(
							`verbal_confirmation_cache_removed shiftId=${shiftId} recordingId=${clientVerbalConfirmationRecordingId} reason=uploaded`,
						);
					} else {
						logger.info(
							`verbal_confirmation_upload_deferred shiftId=${shiftId} recordingId=${clientVerbalConfirmationRecordingId} reason=offline`,
						);
						deferRemoteSave();
						return;
					}
				} catch (error: unknown) {
					logger.warn(
						`Verbal confirmation upload failed for shift ${shiftId}, will retry during sync: ${error instanceof Error ? error.message : error}`,
					);
					deferRemoteSave();
					return;
				}
			}

			// check out requests can take a really long time when connection is poor
			// so to improve UX we'll just arbitrarily timeout and treat it like an offline scenario.
			// Create a timeout promise that rejects after 10 seconds
			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(() => {
					reject(new Error("Network timeout - request took too long"));
				}, 10000);
			});

			// Race between the mutation and the timeout
			try {
				const result = await Promise.race([
					remoteSaveCareSheetCheckOut.mutateAsync(variables),
					timeoutPromise,
				]);
				return result;
			} catch (error) {
				// If it's a timeout error, handle it as a network error
				if (
					error instanceof Error &&
					error.message.includes("Network timeout")
				) {
					logger.warn(
						`checkout_submission_deferred shiftId=${shiftId} reason=timeout`,
					);

					deferRemoteSave();
					// Don't throw the error to prevent the mutation from showing error state
					return;
				}
				logger.error(
					`checkout_submission_failed shiftId=${shiftId} error=${error instanceof Error ? error.message : error}`,
				);
				// Re-throw other errors to be handled by the mutation's onError
				throw error;
			}
		},
		onError(e) {
			console.error(e);
			logger.error(`localSaveCareSheetCheckOut error: ${e}`);
		},

		networkMode: "always",
	});

	useEffect(() => {
		if (checkoutDetails) {
			if (initializedStateRef.current) {
				return;
			}
			initializedStateRef.current = true;
			const initialTasks = (checkoutDetails.shift.completed_tasks || []).map(
				(task) => ({
					...task,
				}),
			);
			setCompletedTasks(initialTasks);
			setInitialCompletedTasks(initialTasks);
			if (checkoutDetails.shift.client_initials) {
				setClientInitials(checkoutDetails.shift.client_initials);
				setClientVerificationMethod("signature");
			}
			if (checkoutDetails.shift.client_signature) {
				setClientSignature(checkoutDetails.shift.client_signature);
				setClientVerificationMethod("signature");
			}
			if (checkoutDetails.shift.caregiver_signature) {
				setCaregiverSignature(checkoutDetails.shift.caregiver_signature);
			}
			if (checkoutDetails.shift.caregiver_initials) {
				setCaregiverInitials(checkoutDetails.shift.caregiver_initials);
			}
			if (checkoutDetails.shift.transportation_data) {
				const transportData = checkoutDetails.shift.transportation_data;
				setTransportationData(transportData);
				setInitialTransportationData(transportData);
				setIncludeTransportation(true);
				setInitialIncludeTransportation(true);
			}
			if (checkoutDetails.shift.timesheets?.check_in) {
				const checkInTime = dayjs
					.utc(checkoutDetails.shift.timesheets.check_in)
					.local()
					.format("HH:mm");
				setCheckIn(checkInTime);
				setOriginalCheckIn(checkInTime);
				setCheckInDateValue(
					dayjs
						.utc(checkoutDetails.shift.timesheets.check_in)
						.local()
						.format("YYYY-MM-DD"),
				);
			} else if (checkoutDetails.evvCheckInEnabled) {
				setCheckIn(getCurrentTime());
				// EVV check-in defaults to today's date, matching getCurrentTime() above
				setCheckInDateValue(dayjs().format("YYYY-MM-DD"));
			} else {
				// Manual check-in with no existing timesheet: default to the scheduled start date
				setCheckInDateValue(
					dayjs
						.utc(checkoutDetails.shift.start_timestamp)
						.local()
						.format("YYYY-MM-DD"),
				);
			}

			if (checkoutDetails.shift.timesheets?.check_out) {
				const checkOutTime = dayjs
					.utc(checkoutDetails.shift.timesheets.check_out)
					.local()
					.format("HH:mm");
				setCheckOut(checkOutTime);
				setOriginalCheckOut(checkOutTime);
				setCheckOutDateValue(
					dayjs
						.utc(checkoutDetails.shift.timesheets.check_out)
						.local()
						.format("YYYY-MM-DD"),
				);
			} else if (
				checkoutDetails.evvCheckOutEnabled &&
				checkoutDetails.shift.timesheets?.check_in
			) {
				setCheckOut(getCurrentTime());
				// EVV check-out defaults to today's date, matching getCurrentTime() above
				setCheckOutDateValue(dayjs().format("YYYY-MM-DD"));
			} else if (checkoutDetails.shift.timesheets?.check_in) {
				// Manual check-out with a recorded check-in but no check-out yet:
				// default to the scheduled end date
				setCheckOutDateValue(
					dayjs
						.utc(checkoutDetails.shift.end_timestamp)
						.local()
						.format("YYYY-MM-DD"),
				);
			}
			if (checkoutDetails.shift.caregiver_comment) {
				setCaregiverComment(checkoutDetails.shift.caregiver_comment);
			}
			if (checkoutDetails.shift.client_unable_to_sign_reason) {
				setClientUnableToSignReason(
					checkoutDetails.shift.client_unable_to_sign_reason,
				);
				setClientVerificationMethod("unableToVerifyReason");
			}
			if (
				checkoutDetails.shift.client_verbal_confirmation_recording_id &&
				checkoutDetails.shift.client_verbal_confirmation_recording_content_type
			) {
				setSavedClientVerbalConfirmationRecording({
					id: checkoutDetails.shift.client_verbal_confirmation_recording_id,
					contentType:
						checkoutDetails.shift
							.client_verbal_confirmation_recording_content_type,
				});
				setClientVerificationMethod("voiceRecording");
			}
		}
	}, [checkoutDetails]);

	useEffect(() => {
		if (!checkoutDetails || clientVerificationMethod) {
			return;
		}
		if (
			checkoutDetails.shift.client_initials ||
			checkoutDetails.shift.client_signature ||
			checkoutDetails.shift.client_unable_to_sign_reason ||
			checkoutDetails.shift.client_verbal_confirmation_recording_id
		) {
			return;
		}

		if (checkoutDetails.shift.clients.evv_voice_recording_enabled) {
			setClientVerificationMethod("voiceRecording");
			return;
		}
		if (
			checkoutDetails.shift.clients.evv_signature_enabled &&
			(checkoutDetails.clientInitialsRequired ||
				checkoutDetails.clientSignatureRequired)
		) {
			setClientVerificationMethod("signature");
			return;
		}
		if (
			(checkoutDetails.clientInitialsRequired ||
				checkoutDetails.clientSignatureRequired) &&
			checkoutDetails.shift.clients.evv_unable_to_verify_reason_enabled
		) {
			setClientVerificationMethod("unableToVerifyReason");
			return;
		}
		// Old offline cache entries can have every verification method disabled.
		setClientVerificationMethod("signature");
	}, [checkoutDetails, clientVerificationMethod]);

	const getExperienceRatingButtonColor = (score: number) => {
		if (score <= 3) return "red";
		if (score <= 5) return "orange";
		if (score <= 7) return "yellow";
		return "green";
	};

	// Check for changes in completed tasks or transportation data
	useEffect(() => {
		if (
			initialCompletedTasks.length === 0 &&
			initialTransportationData.length === 0
		) {
			return;
		}

		// Check for tasks changes
		const tasksChanged = () => {
			if (completedTasks.length !== initialCompletedTasks.length) {
				return true;
			}

			// Check if any tasks were added or removed
			for (const task of completedTasks) {
				const initialTask = initialCompletedTasks.find(
					(t) => t.category === task.category && t.name === task.name,
				);

				if (!initialTask) {
					return true; // New task added
				}

				// Check if task details changed
				if (
					task.notes !== initialTask.notes ||
					task.timeMinutes !== initialTask.timeMinutes
				) {
					return true;
				}
			}

			// Check if any tasks were removed
			for (const initialTask of initialCompletedTasks) {
				const currentTask = completedTasks.find(
					(t) =>
						t.category === initialTask.category && t.name === initialTask.name,
				);

				if (!currentTask) {
					return true; // Task was removed
				}
			}

			return false;
		};

		// Check for transportation changes
		const transportationChanged = () => {
			if (includeTransportation !== initialIncludeTransportation) {
				return true;
			}

			if (includeTransportation && initialIncludeTransportation) {
				if (transportationData.length !== initialTransportationData.length) {
					return true;
				}

				// Check each transportation entry for changes
				for (let i = 0; i < transportationData.length; i++) {
					const current = transportationData[i];
					const initial = initialTransportationData[i];

					if (!initial) return true;

					if (
						current.startTime !== initial.startTime ||
						current.endTime !== initial.endTime ||
						current.totalMiles !== initial.totalMiles ||
						current.destinationAndPurpose !== initial.destinationAndPurpose ||
						current.errandsTime !== initial.errandsTime ||
						current.activitiesTime !== initial.activitiesTime ||
						current.wasClientWithYou !== initial.wasClientWithYou
					) {
						return true;
					}
				}
			}

			return false;
		};

		const anyChanges = tasksChanged() || transportationChanged();
		setHasFormChanges(anyChanges);
	}, [
		completedTasks,
		initialCompletedTasks,
		includeTransportation,
		initialIncludeTransportation,
		transportationData,
		initialTransportationData,
	]);

	useEffect(() => {
		if (checkoutDetails && transportationData.length > 0) {
			const shiftStart = new Date(checkoutDetails.shift.start_timestamp);
			const shiftEnd = new Date(checkoutDetails.shift.end_timestamp);

			const newTimeWarnings = {
				startBeforeScheduled: [] as boolean[],
				endAfterScheduled: [] as boolean[],
				startAfterEnd: [] as boolean[],
			};

			transportationData.forEach((entry, index) => {
				if (entry.startTime && entry.endTime) {
					const [startHours, startMinutes] = entry.startTime.split(":");
					const [endHours, endMinutes] = entry.endTime.split(":");

					const transportStart = new Date(shiftStart);
					transportStart.setHours(
						Number.parseInt(startHours),
						Number.parseInt(startMinutes),
					);

					const transportEnd = new Date(shiftEnd);
					transportEnd.setHours(
						Number.parseInt(endHours),
						Number.parseInt(endMinutes),
					);

					newTimeWarnings.startBeforeScheduled[index] =
						transportStart < shiftStart;
					newTimeWarnings.endAfterScheduled[index] = transportEnd > shiftEnd;
					newTimeWarnings.startAfterEnd[index] = transportStart > transportEnd;
				}
			});

			setTimeWarnings(newTimeWarnings);
		}
	}, [checkoutDetails, transportationData]);

	useEffect(() => {
		innerScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
	}, [currentStep]);

	const addTransportationEntry = () => {
		if (transportationData.length < maxTransportationEntries) {
			clearSignaturesAndConfirmations();
			setTransportationData([
				...transportationData,
				{
					startTime: "",
					endTime: "",
					totalMiles: 0,
					destinationAndPurpose: "",
					errandsTime: 0,
					activitiesTime: 0,
					wasClientWithYou: undefined,
				},
			]);
		}
	};

	const removeTransportationEntry = (index: number) => {
		clearSignaturesAndConfirmations();
		setTransportationData(transportationData.filter((_, i) => i !== index));
	};

	const updateTransportationEntry = (
		index: number,
		updates: Partial<TransportationEntry>,
	) => {
		clearSignaturesAndConfirmations();
		setTransportationData(
			transportationData.map((entry, i) =>
				i === index ? { ...entry, ...updates } : entry,
			),
		);
	};

	const toggleTask = (task: { category: string; name: string }) => {
		clearSignaturesAndConfirmations();

		const taskStat = checkoutDetails?.taskStats.find(
			(stat) => stat.category === task.category && stat.name === task.name,
		);
		setCompletedTasks((prev) => {
			const exists = prev.find(
				(t) => t.category === task.category && t.name === task.name,
			);
			if (exists) {
				return prev.filter(
					(t) => t.category !== task.category || t.name !== task.name,
				);
			}

			if (taskStat) {
				const simulatedStats = simulateTaskStats(
					taskStat,
					{},
					checkoutDetails?.shift.client_addresses?.timezone ||
						checkoutDetails?.shift.clients.branches?.timezone ||
						"America/New_York",
					checkoutDetails?.shift.start_timestamp ?? "",
					checkoutDetails?.shift.completed_tasks?.some(
						(t) => t.category === task.category && t.name === task.name,
					),
					checkoutDetails?.shift.completed_tasks?.find(
						(t) => t.category === task.category && t.name === task.name,
					)?.timeMinutes || 0,
				);

				return [
					...prev,
					{
						category: task.category,
						name: task.name,
						simulatedStats,
					},
				];
			}

			return [...prev, { category: task.category, name: task.name }];
		});
	};

	const updateTaskNotes = (
		task: { category: string; name: string },
		notes: string,
	) => {
		clearSignaturesAndConfirmations();
		setCompletedTasks((prev) => {
			return prev.map((t) => {
				if (t.category === task.category && t.name === task.name) {
					return { ...t, notes };
				}
				return t;
			});
		});
	};

	const updateTaskTime = (
		task: { category: string; name: string },
		timeMinutes: number,
	) => {
		clearSignaturesAndConfirmations();
		const taskStat = checkoutDetails?.taskStats.find(
			(stat) => stat.category === task.category && stat.name === task.name,
		);
		setCompletedTasks((prev) => {
			return prev.map((t) => {
				if (t.category === task.category && t.name === task.name) {
					const simulatedStats = taskStat
						? simulateTaskStats(
								taskStat,
								{ timeMinutes },
								checkoutDetails?.shift.client_addresses?.timezone ||
									checkoutDetails?.shift.clients.branches?.timezone ||
									"America/New_York",
								checkoutDetails?.shift.start_timestamp ?? "",
								checkoutDetails?.shift.completed_tasks?.some(
									(t) => t.category === task.category && t.name === task.name,
								),
								checkoutDetails?.shift.completed_tasks?.find(
									(t) => t.category === task.category && t.name === task.name,
								)?.timeMinutes || 0,
							)
						: undefined;

					return {
						...t,
						timeMinutes,
						simulatedStats,
					};
				}
				return t;
			});
		});
	};

	// Get all steps in order, filtering out transportation if not supported
	const getSteps = (): FormStep[] => {
		const baseSteps: FormStep[] = ["timeEntry", "tasks"];
		if (checkoutDetails?.transportationDataSupported) {
			baseSteps.push("transportation");
		}
		baseSteps.push("comment", "review");
		return baseSteps;
	};

	const steps = getSteps();
	const currentStepIndex = steps.indexOf(currentStep);
	const isLastStep = currentStepIndex === steps.length - 1;
	const isFirstStep = currentStepIndex === 0;

	const goToNextStep = () => {
		if (!isLastStep) {
			setCurrentStep(steps[currentStepIndex + 1]);
		}
	};

	const goToPreviousStep = () => {
		if (isFirstStep) {
			navigateBack(navigate, ROUTES.CAREGIVER_APP.ROOT);
		} else {
			setCurrentStep(steps[currentStepIndex - 1]);
		}
	};

	if (
		checkOutDetailInitialLoading ||
		(!checkoutDetails && !checkOutDetailLoadError)
	) {
		return (
			<Flex direction="column" style={{ height: "100vh" }}>
				<div style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
					<Group
						align="center"
						style={{ height: 64, padding: "0 var(--mantine-spacing-md)" }}
					>
						<Title order={3} style={{ flex: 1, textAlign: "center" }}>
							Care Sheet Checkout
						</Title>
						<div style={{ width: 48 }} />
					</Group>
				</div>
				<Flex style={{ flex: 1 }} align="center" justify="center">
					<Stack align="center" gap="md">
						<LoadingOverlay visible={true} />
						<Text size="lg" fw={500} c="dimmed">
							Loading details...
						</Text>
					</Stack>
				</Flex>
			</Flex>
		);
	}

	if (checkOutDetailLoadError && !checkoutDetails) {
		return (
			<Container size="sm" py="xl">
				<Card withBorder p="sm">
					<Stack align="center" gap="md" py="md">
						<Text size="lg" fw={500}>
							Error loading checkout details
						</Text>
						{checkOutDetailLoadError && (
							<Text c="red" size="sm">
								{checkOutDetailLoadError.message}
							</Text>
						)}
						<Button component={Link} to="/app">
							Return to Home
						</Button>
					</Stack>
				</Card>
			</Container>
		);
	}

	if (
		checkoutDetails?.shift.clients.evv_voice_recording_enabled &&
		microphonePermissionGranted === false
	) {
		const returnTo = `/app/care-sheets/${careSheetId}/shifts/${shiftId}/checkout`;
		const navigateTo = `${ROUTES.CAREGIVER_APP.ONBOARDING}?step=microphone-info&returnTo=${encodeURIComponent(returnTo)}`;

		if (!microphoneSkipped) {
			return <Navigate to={navigateTo} replace />;
		}

		if (microphoneSkipped) {
			return (
				<IvrInstructionsModal
					opened
					onClose={() => {
						navigateBack(navigate, ROUTES.CAREGIVER_APP.ROOT);
					}}
					additionalActions={
						<Button
							variant="light"
							leftSection={<IconMicrophone size={20} />}
							size="md"
							onClick={() => {
								navigate(navigateTo, { replace: true });
							}}
						>
							Enable in-app recording instead
						</Button>
					}
				/>
			);
		}
	}

	const handleSubmit = () => {
		localSaveCareSheetCheckOut.mutate();
	};

	const missingRequiredNotes = checkoutDetails.assignedTasks?.some((task) => {
		if (!task.noteRequired) {
			return false;
		}

		const completedTask = completedTasks.find(
			(t) => t.category === task.category && t.name === task.name,
		);
		if (!completedTask) {
			return false;
		}
		return !completedTask.notes || completedTask.notes.length === 0;
	});

	const missingRequiredTimes = checkoutDetails.assignedTasks?.some((task) => {
		if (!task.timeRequired) {
			return false;
		}

		const completedTask = completedTasks.find(
			(t) => t.category === task.category && t.name === task.name,
		);
		if (!completedTask) {
			return false;
		}
		return !completedTask.timeMinutes;
	});

	const missingRequiredTransportationData =
		checkoutDetails.transportationDataSupported &&
		includeTransportation &&
		(transportationData.length === 0 ||
			transportationData.some(
				(entry) =>
					!entry.startTime ||
					!entry.endTime ||
					entry.totalMiles === undefined ||
					!entry.destinationAndPurpose ||
					entry.errandsTime === undefined ||
					entry.activitiesTime === undefined ||
					entry.wasClientWithYou === undefined ||
					(entry.errandsTime === 0 && entry.activitiesTime === 0),
			));

	const hasInvalidTimes = timeWarnings.startAfterEnd?.some(
		(warning) => warning,
	);

	const disallowedTasksCompleted = completedTasks.some((t) =>
		checkoutDetails?.disallowedTasks?.some((t2) => t2 === t.name),
	);

	const hasComplianceIssues = () => {
		const hasTaskIssues = completedTasks.some(
			(task) =>
				task.simulatedStats?.isOverMinutes ||
				task.simulatedStats?.isOverOccurrences ||
				task.simulatedStats?.isWrongDay,
		);

		const hasTransportIssues =
			includeTransportation && !checkoutDetails?.transportationAllowed;

		// Check for missing required tasks that should be performed on this day
		const hasMissingRequiredTasks = () => {
			// Get current day of the week (0 = Sunday, 1 = Monday, etc.)
			const currentDayOfWeek = dayjs(
				checkoutDetails.shift.start_timestamp,
			).day();

			// Check each assigned task to see if it's required for today but not completed
			return checkoutDetails.assignedTasks?.some((task) => {
				// Check if task has specific days configured
				const taskStat = checkoutDetails.taskStats.find(
					(stat) => stat.category === task.category && stat.name === task.name,
				);

				// Determine if task is required for today based on frequency/allowed days
				let isRequiredForToday = false;

				// If task has allowed days, check if today is one of those days
				if (taskStat?.allowedDays?.length) {
					isRequiredForToday = taskStat.allowedDays.includes(currentDayOfWeek);
				} else if (
					taskStat?.occurrencesFrequency === "perDay" &&
					!taskStat?.allowedDays?.length &&
					taskStat?.occurrencesLimit !== undefined &&
					taskStat?.occurrencesLimit !== null &&
					taskStat?.occurrencesLimit > 0
				) {
					// If task frequency is daily, it's required every day
					isRequiredForToday = true;
				}

				// If not required for today, skip
				if (!isRequiredForToday) {
					return false;
				}

				// Check if the task was completed
				const isCompleted = completedTasks.some(
					(t) => t.category === task.category && t.name === task.name,
				);

				// Return true if task is required today but not completed
				return !isCompleted;
			});
		};

		// Check if minimum personal task count is not met
		const hasInsufficientPersonalTasks = () => {
			if (!checkoutDetails?.minimumPersonalTaskCount) {
				return false;
			}

			const personalCareTasksCompleted = completedTasks.filter(
				(task) => task.category === "Personal Care",
			).length;

			return (
				personalCareTasksCompleted < checkoutDetails.minimumPersonalTaskCount
			);
		};

		return (
			hasTransportIssues ||
			hasTaskIssues ||
			hasMissingRequiredTasks() ||
			hasInsufficientPersonalTasks() ||
			disallowedTasksCompleted
		);
	};

	const checkInDateTime =
		checkIn && checkInDateValue
			? combineDateAndTime(checkInDateValue, checkIn)
			: null;
	const checkOutDateTime =
		checkOut && checkOutDateValue
			? combineDateAndTime(checkOutDateValue, checkOut)
			: null;

	const checkOutTimeNotAfterCheckIn =
		checkInDateTime &&
		checkOutDateTime &&
		!checkInDateTime.isBefore(checkOutDateTime);

	const checkInTooEarly = checkoutDetails.checkInEarliestAllowedTime
		? dayjs().isBefore(dayjs(checkoutDetails.checkInEarliestAllowedTime))
		: false;

	const checkOutTooEarly = checkoutDetails.checkOutEarliestAllowedTime
		? dayjs().isBefore(dayjs(checkoutDetails.checkOutEarliestAllowedTime))
		: false;

	const clientSignatureVerificationEnabled =
		checkoutDetails.shift.clients.evv_signature_enabled &&
		(checkoutDetails.clientInitialsRequired ||
			checkoutDetails.clientSignatureRequired);
	const clientVoiceVerificationEnabled =
		checkoutDetails.shift.clients.evv_voice_recording_enabled;
	const clientUnableToVerifyEnabled =
		(checkoutDetails.clientInitialsRequired ||
			checkoutDetails.clientSignatureRequired) &&
		checkoutDetails.shift.clients.evv_unable_to_verify_reason_enabled;
	const clientConfirmationSatisfied =
		//initials satisfied
		(checkoutDetails.clientInitialsRequired &&
			clientSignatureVerificationEnabled &&
			!!clientInitials) ||
		//signature satisfied
		(checkoutDetails.clientSignatureRequired &&
			clientSignatureVerificationEnabled &&
			!!clientSignature) ||
		// voice recording satisfied
		((checkoutDetails.clientSignatureRequired ||
			checkoutDetails.clientInitialsRequired) &&
			checkoutDetails.shift.clients.evv_voice_recording_enabled &&
			(!!clientVerbalConfirmationRecording ||
				!!savedClientVerbalConfirmationRecording)) ||
		// unable to verify satisfied
		((checkoutDetails.clientSignatureRequired ||
			checkoutDetails.clientInitialsRequired) &&
			checkoutDetails.shift.clients.evv_unable_to_verify_reason_enabled &&
			!!clientUnableToSignReason);
	const verificationIncomplete =
		!clientConfirmationSatisfied ||
		(checkoutDetails.caregiverInitialsRequired && !caregiverInitials) ||
		(checkoutDetails.caregiverSignatureRequired && !caregiverSignature);
	const clientVerificationRequired =
		clientSignatureVerificationEnabled ||
		clientVoiceVerificationEnabled ||
		clientUnableToVerifyEnabled;
	const clientVerificationMethodCount = [
		clientSignatureVerificationEnabled,
		clientVoiceVerificationEnabled,
		clientUnableToVerifyEnabled,
	].filter(Boolean).length;
	// Group tasks by category
	const tasksByCategory = (checkoutDetails.assignedTasks || []).reduce(
		(acc, task) => {
			if (!acc[task.category]) {
				acc[task.category] = [];
			}
			acc[task.category].push(task);
			return acc;
		},
		{} as Record<string, NonNullable<typeof checkoutDetails.assignedTasks>>,
	);

	const checkInDiff = checkInDateTime
		? Math.abs(
				checkInDateTime.diff(
					dayjs.utc(checkoutDetails.shift.start_timestamp).local(),
					"minutes",
				),
			)
		: null;

	const checkOutDiff = checkOutDateTime
		? Math.abs(
				checkOutDateTime.diff(
					dayjs.utc(checkoutDetails.shift.end_timestamp).local(),
					"minutes",
				),
			)
		: null;

	// EVV check-in/out times are captured automatically, so they're excluded from these checks.
	const checkInSignificantlyDifferent =
		checkInDiff !== null &&
		checkInDiff > 15 &&
		!checkoutDetails.evvCheckInEnabled &&
		!checkoutDetails.isEvvCheckIn;
	const checkOutSignificantlyDifferent =
		checkOutDiff !== null &&
		checkOutDiff > 15 &&
		!checkoutDetails.evvCheckOutEnabled &&
		!checkoutDetails.isEvvCheckOut;
	const hasSignificantTimeDifference =
		checkInSignificantlyDifferent || checkOutSignificantlyDifferent;

	// Advances past the time entry step (or saves check-in-only progress), skipping
	// the confirmation modal. Called directly when times match schedule, or after
	// the caregiver confirms they want to proceed despite a significant time difference.
	const proceedFromTimeEntryStep = () => {
		if (checkOut) {
			if (currentStep === "timeEntry" && checkoutDetails.evv_consent_granted) {
				// related to: VCG-613
				// we request gps here on check out
				// to provide more time for gps capture
				// hopefuly reducing wait time on check out complete
				// and avoiding certain gps capture errors
				getGpsLocation()
					.then((pos) => {
						preemptiveGpsPosition.current = pos;
					})
					.catch((e: unknown) => {
						logger.error(e instanceof Error ? e.message : "Unknown GPS Error");
					});
			}
			goToNextStep();
		} else {
			localSaveCareSheetCheckOut.mutate();
		}
	};

	const getStepTitle = (step: FormStep): string => {
		switch (step) {
			case "timeEntry":
				return "Time Entry";
			case "tasks":
				return "Tasks";
			case "transportation":
				return "Transportation";
			case "comment":
				return "Comment";
			case "review":
				return "Review & Sign";
			default:
				return "";
		}
	};

	const isStepValid = (step: FormStep): boolean => {
		switch (step) {
			case "timeEntry":
				return !(
					!checkIn ||
					checkInTooEarly ||
					(checkOut && checkOutTooEarly) ||
					checkOutTimeNotAfterCheckIn
				);
			case "tasks":
				return !(
					checkOut &&
					(completedTasks.length === 0 ||
						missingRequiredNotes ||
						missingRequiredTimes)
				);
			case "transportation":
				return !missingRequiredTransportationData && !hasInvalidTimes;
			case "comment":
				return !(
					(hasComplianceIssues() || hasFormChanges) &&
					(!caregiverComment || caregiverComment.trim().length < 10)
				);
			case "review":
				return !verificationIncomplete;
			default:
				return true;
		}
	};

	return (
		<Flex direction="column" w="100%">
			<div style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
				<Group
					align="center"
					style={{ height: 80, padding: "0 var(--mantine-spacing-md)" }}
				>
					<Group gap="sm">
						<ActionIcon variant="subtle" onClick={goToPreviousStep} size="lg">
							<IconArrowLeft />
						</ActionIcon>
					</Group>
					<Stack gap={0} style={{ flex: 1, textAlign: "center" }}>
						<Title order={4}>
							{!originalCheckIn ? "Check In" : "Check Out"}
						</Title>
						<Text size="xs" c="dimmed">
							{checkoutDetails.shift.clients.first_name}{" "}
							{checkoutDetails.shift.clients.last_name} |{" "}
							{checkInDateTime ? (
								checkInDateTime.format("MM/DD")
							) : (
								<LocalizedTimeText
									value={checkoutDetails.shift.start_timestamp}
									format="MM/DD"
								/>
							)}{" "}
							| {checkInDateTime?.format("h:mm A") || ""}-{" "}
							{checkOutDateTime?.format("h:mm A") || ""}
						</Text>
						<Progress
							value={(currentStepIndex / steps.length) * 100}
							mt="xs"
							size="sm"
							title={`Step ${currentStepIndex + 1} of ${steps.length}: ${getStepTitle(currentStep)}`}
						/>
					</Stack>
					<div style={{ width: 48 }} />
				</Group>
			</div>
			{currentStep === "timeEntry" && (
				<>
					<Flex
						direction="column"
						style={{ flex: 1, overflowY: "auto" }}
						ref={innerScrollRef}
					>
						<Box
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								padding: "var(--mantine-spacing-md)",
							}}
						>
							<Box style={{ width: "100%", maxWidth: "800px" }}>
								<Stack gap="md">
									<Alert color="yellow" icon={<IconAlertCircle size={16} />}>
										The client must review the visit details and sign before
										check out can be completed or edited.
									</Alert>

									{checkoutDetails.carePlanRequired &&
										!checkoutDetails.shift.care_plan_id && (
											<Alert color="red" icon={<IconAlertCircle size={16} />}>
												No care plan is available for this visit. Contact your
												supervisor for assistance.
											</Alert>
										)}

									<Card withBorder p="sm">
										<Stack gap="md">
											<Title order={4}>Time Entries</Title>

											<Grid>
												<Grid.Col span={6}>
													<Stack gap="xs">
														<Text fw={500}>Scheduled Start</Text>
														<Text size="sm">
															<LocalizedTimeText
																value={checkoutDetails.shift.start_timestamp}
																format="MM/DD h:mm A"
															/>
														</Text>
													</Stack>
												</Grid.Col>

												<Grid.Col span={6}>
													<Stack gap="xs">
														<Text fw={500}>Scheduled End</Text>
														<Text size="sm">
															<LocalizedTimeText
																value={checkoutDetails.shift.end_timestamp}
																format="MM/DD h:mm A"
															/>
														</Text>
													</Stack>
												</Grid.Col>
											</Grid>

											<Grid>
												<Grid.Col span={6}>
													<Stack gap="xs">
														<Text
															fw={500}
															c={
																checkoutDetails.evvCheckInEnabled ||
																checkoutDetails.isEvvCheckIn
																	? "green"
																	: undefined
															}
														>
															{checkoutDetails.evvCheckInEnabled ||
															checkoutDetails.isEvvCheckIn
																? "EVV Check In"
																: "Check In"}
														</Text>
														<DateInput
															aria-label="Check In Date"
															valueFormat="MM/DD/YYYY"
															value={
																checkInDateValue
																	? new Date(`${checkInDateValue}T00:00:00`)
																	: null
															}
															onChange={(date) => {
																clearSignaturesAndConfirmations();
																setCheckInDateValue(
																	date
																		? dayjs(date).format("YYYY-MM-DD")
																		: null,
																);
															}}
															readOnly={
																checkoutDetails.checkInLocked ||
																checkoutDetails.evvCheckInEnabled
															}
															disabled={checkoutDetails.checkInLocked}
														/>
														<Input
															type="time"
															value={checkIn || ""}
															onChange={(e) => {
																clearSignaturesAndConfirmations();
																setCheckIn(e.target.value);
															}}
															readOnly={
																checkoutDetails.checkInLocked ||
																checkoutDetails.evvCheckInEnabled
															}
															disabled={checkoutDetails.checkInLocked}
															required
														/>
														{checkInSignificantlyDifferent && (
															<Group gap="xs">
																<IconAlertCircle
																	size={16}
																	color="var(--mantine-color-yellow-6)"
																/>
																<Text fw={500} c="yellow.6">
																	Check in is different from schedule by more
																	than 15 minutes
																</Text>
															</Group>
														)}
														{checkInTooEarly && (
															<Group gap="xs">
																<IconAlertCircle
																	size={16}
																	color="var(--mantine-color-red-6)"
																/>
																<Text fw={500} c="red.6">
																	You can't check in before{" "}
																	{dayjs
																		.utc(
																			checkoutDetails.checkInEarliestAllowedTime,
																		)
																		.local()
																		.format("h:mm A")}
																</Text>
															</Group>
														)}
													</Stack>
												</Grid.Col>

												<Grid.Col span={6}>
													<Stack gap="xs">
														<Text
															fw={500}
															c={
																checkoutDetails.evvCheckOutEnabled ||
																checkoutDetails.isEvvCheckOut
																	? "green"
																	: undefined
															}
														>
															{checkoutDetails.evvCheckOutEnabled ||
															checkoutDetails.isEvvCheckOut
																? "EVV Check Out"
																: "Check Out"}
														</Text>
														<DateInput
															aria-label="Check Out Date"
															valueFormat="MM/DD/YYYY"
															value={
																checkOutDateValue
																	? new Date(`${checkOutDateValue}T00:00:00`)
																	: null
															}
															onChange={(date) => {
																clearSignaturesAndConfirmations();
																setCheckOutDateValue(
																	date
																		? dayjs(date).format("YYYY-MM-DD")
																		: null,
																);
															}}
															readOnly={
																checkoutDetails.checkOutLocked ||
																checkoutDetails.evvCheckOutEnabled ||
																!checkoutDetails.shift.timesheets?.check_in
															}
															disabled={
																checkoutDetails.checkOutLocked ||
																!checkoutDetails.shift.timesheets?.check_in
															}
														/>
														<Input
															type="time"
															value={checkOut || ""}
															onChange={(e) => {
																clearSignaturesAndConfirmations();
																setCheckOut(e.target.value);
															}}
															readOnly={
																checkoutDetails.checkOutLocked ||
																checkoutDetails.evvCheckOutEnabled ||
																!checkoutDetails.shift.timesheets?.check_in
															}
															disabled={
																checkoutDetails.checkOutLocked ||
																!checkoutDetails.shift.timesheets?.check_in
															}
														/>
														{checkOutSignificantlyDifferent && (
															<Group gap="xs">
																<IconAlertCircle
																	size={16}
																	color="var(--mantine-color-yellow-6)"
																/>
																<Text fw={500} c="yellow.6">
																	Check out is different from schedule by more
																	than 15 minutes
																</Text>
															</Group>
														)}
														{checkOut && checkOutTooEarly && (
															<Group gap="xs">
																<IconAlertCircle
																	size={16}
																	color="var(--mantine-color-red-6)"
																/>
																<Text fw={500} c="red.6">
																	You can't check out before{" "}
																	{dayjs
																		.utc(
																			checkoutDetails.checkOutEarliestAllowedTime,
																		)
																		.local()
																		.format("h:mm A")}
																</Text>
															</Group>
														)}
													</Stack>
												</Grid.Col>
											</Grid>

											{checkOutTimeNotAfterCheckIn && (
												<Group gap="xs">
													<IconAlertCircle
														size={16}
														color="var(--mantine-color-red-6)"
													/>
													<Text fw={500} c="red.6">
														Check in time must be before check out time
													</Text>
												</Group>
											)}
										</Stack>
									</Card>

									{/* Care Plan Link */}
									{checkoutDetails.shift.care_plan_id && (
										<Card withBorder p="sm">
											<Group p="sm" gap="xs">
												<Link
													to={ROUTES.CAREGIVER_APP.CARE_PLANS(
														checkoutDetails.shift.care_plan_id,
													)}
													style={{
														color: "var(--mantine-color-blue-6)",
														textDecoration: "underline",
														display: "flex",
														gap: "8px",
														alignItems: "center",
													}}
												>
													<IconReportMedical size={24} />
													<span>View Care Plan</span>
												</Link>
											</Group>
										</Card>
									)}
									<IvrInstructionsButton />
								</Stack>
							</Box>
						</Box>
					</Flex>

					<Box
						style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
						p="sm"
					>
						<Box style={{ maxWidth: "800px", margin: "0 auto", width: "100%" }}>
							<Button
								onClick={() => {
									if (hasSignificantTimeDifference) {
										setTimeDifferenceModalOpened(true);
										return;
									}
									proceedFromTimeEntryStep();
								}}
								fullWidth
								disabled={
									!isStepValid("timeEntry") ||
									localSaveCareSheetCheckOut.isPending ||
									checkoutDetails.careSheetLocked ||
									Boolean(
										checkoutDetails.carePlanRequired &&
											!checkoutDetails.shift.care_plan_id,
									)
								}
								loading={localSaveCareSheetCheckOut.isPending}
							>
								{checkoutDetails.carePlanRequired &&
								!checkoutDetails.shift.care_plan_id
									? "Care Plan Required"
									: checkoutDetails.careSheetLocked
										? "Care Sheet Locked"
										: checkOut
											? "Next: Tasks"
											: "Save"}
							</Button>
						</Box>
					</Box>
				</>
			)}
			{currentStep === "tasks" && (
				<>
					<Flex
						direction="column"
						style={{ flex: 1, overflowY: "auto" }}
						ref={innerScrollRef}
					>
						<Box
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								padding: "var(--mantine-spacing-md)",
							}}
						>
							<Box style={{ width: "100%", maxWidth: "800px" }}>
								<Stack gap="md">
									{Object.entries(tasksByCategory).map(([category, tasks]) => (
										<Card key={category} withBorder p="sm">
											<Stack p="sm" gap="md">
												<Title order={4}>{category}</Title>
												<Stack gap="md">
													{tasks.map((task) => {
														const completedTask = completedTasks.find(
															(t) =>
																t.category === task.category &&
																t.name === task.name,
														);
														const previouslyCompletedTask =
															checkoutDetails.shift.completed_tasks?.find(
																(t) =>
																	t.category === task.category &&
																	t.name === task.name,
															);

														const existingTaskStat =
															checkoutDetails.taskStats.find(
																(stat) =>
																	stat.category === task.category &&
																	stat.name === task.name,
															);

														const taskStat =
															completedTask?.simulatedStats ||
															(previouslyCompletedTask &&
															existingTaskStat &&
															!completedTask
																? {
																		...existingTaskStat,
																		totalOccurrences:
																			existingTaskStat.totalOccurrences - 1,
																		totalMinutes:
																			existingTaskStat.totalMinutes -
																			(previouslyCompletedTask?.timeMinutes ||
																				0),
																	}
																: existingTaskStat);
														return (
															<Group
																key={`${task.category}-${task.name}`}
																align="flex-start"
																gap="sm"
															>
																<Checkbox
																	checked={!!completedTask}
																	onChange={() => toggleTask(task)}
																	aria-label={`Mark ${task.name} as ${completedTask ? "incomplete" : "completed"}`}
																/>
																<Stack gap={0} style={{ flex: 1 }}>
																	<Stack gap={4}>
																		<Text fw={500}>
																			{task.name}
																			{isTaskRequiredForToday(
																				task,
																				checkoutDetails,
																			) && (
																				<Text span c="red" ml={4}>
																					*
																				</Text>
																			)}
																		</Text>
																		{task.note ? (
																			<Text
																				size="sm"
																				style={{
																					fontStyle: "italic",
																				}}
																			>
																				{task.note}
																			</Text>
																		) : null}
																		{taskStat && (
																			<Text size="sm" fw={400} c="dimmed">
																				Week to date:{" "}
																				{taskStat.totalOccurrences} times,{" "}
																				{taskStat.totalMinutes} minutes
																			</Text>
																		)}
																	</Stack>
																	{isTaskRequiredForToday(
																		task,
																		checkoutDetails,
																	) && (
																		<Text size="sm" c="red">
																			Scheduled for today
																		</Text>
																	)}
																	{completedTask && (
																		<TaskWarnings task={completedTask} />
																	)}
																	{task.noteRequired && completedTask && (
																		<Textarea
																			mt="xs"
																			placeholder="Add notes (required)"
																			value={completedTask.notes || ""}
																			onChange={(e) =>
																				updateTaskNotes(task, e.target.value)
																			}
																			maxLength={250}
																			minLength={1}
																			required
																		/>
																	)}
																	{task.timeRequired && completedTask && (
																		<Stack gap="xs" mt="xs">
																			<Text fw={500}>
																				Time (minutes, required)
																			</Text>
																			<NumberInput
																				min={0}
																				value={completedTask.timeMinutes || ""}
																				onChange={(value) =>
																					updateTaskTime(task, Number(value))
																				}
																				required
																			/>
																		</Stack>
																	)}
																</Stack>
															</Group>
														);
													})}
												</Stack>
											</Stack>
										</Card>
									))}

									{/* Missing Required Tasks Warning */}
									<MissingRequiredTasksWarning
										checkoutDetails={checkoutDetails}
										completedTasks={completedTasks}
									/>

									{/* Insufficient Personal Tasks Warning */}
									{checkoutDetails?.minimumPersonalTaskCount &&
									completedTasks.filter(
										(task) => task.category === "Personal Care",
									).length < checkoutDetails.minimumPersonalTaskCount ? (
										<Alert color="red" icon={<IconAlertTriangle size={16} />}>
											<Stack gap="xs">
												<Text fw={500}>
													Minimum personal care tasks not met
												</Text>
												<Text size="sm">
													At least {checkoutDetails.minimumPersonalTaskCount}{" "}
													personal care task
													{checkoutDetails.minimumPersonalTaskCount > 1
														? "s"
														: ""}{" "}
													must be completed per visit. Currently completed:{" "}
													{
														completedTasks.filter(
															(task) => task.category === "Personal Care",
														).length
													}
												</Text>
											</Stack>
										</Alert>
									) : null}

									{disallowedTasksCompleted && (
										<Alert color="red" icon={<IconAlertTriangle size={16} />}>
											<Stack gap="xs">
												<Text fw={500}>Disallowed Tasks</Text>
												<Text size="sm">
													{checkoutDetails?.disallowedTasks?.join(", ")} are not
													allowed.
												</Text>
											</Stack>
										</Alert>
									)}
								</Stack>
							</Box>
						</Box>
					</Flex>

					<Box
						style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
						p="sm"
					>
						<Box style={{ maxWidth: "800px", margin: "0 auto", width: "100%" }}>
							<Group gap="md" grow>
								<Button variant="outline" onClick={goToPreviousStep}>
									Back
								</Button>
								<Button onClick={goToNextStep} disabled={!isStepValid("tasks")}>
									Next:{" "}
									{checkoutDetails?.transportationDataSupported
										? "Transportation"
										: "Comment"}
								</Button>
							</Group>
						</Box>
					</Box>
				</>
			)}
			{currentStep === "transportation" && (
				<>
					<Flex
						direction="column"
						style={{ flex: 1, overflowY: "auto" }}
						ref={innerScrollRef}
					>
						<Box
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								padding: "var(--mantine-spacing-md)",
							}}
						>
							<Box style={{ width: "100%", maxWidth: "800px" }}>
								<Stack gap="md">
									<Card withBorder p="sm">
										<Stack p="sm" gap="md">
											<Title order={4}>Transportation</Title>
											<Stack gap="md">
												{!checkoutDetails.transportationAllowed && (
													<Alert
														color="yellow"
														icon={<IconAlertTriangle size={16} />}
													>
														Transportation is not allowed in the care plan
													</Alert>
												)}
											</Stack>
											<Stack gap="xs">
												<Text fw={500}>
													Did you travel for errands or activities during this
													visit?
												</Text>
												<Radio.Group
													value={includeTransportation?.toString()}
													onChange={(value) => {
														setIncludeTransportation(value === "true");
														if (
															value === "true" &&
															transportationData.length === 0
														) {
															addTransportationEntry();
														}
													}}
												>
													<Group>
														<Radio value="true" label="Yes" />
														<Radio value="false" label="No" />
													</Group>
												</Radio.Group>
											</Stack>

											{includeTransportation && (
												<Stack gap="md">
													{transportationData.map((entry, index) => (
														<Card
															key={`transportation-${entry.startTime}-${entry.endTime}-${index}`}
															withBorder
															p="sm"
														>
															<Stack p="sm" gap="md">
																<Group justify="space-between">
																	<Text fw={500}>Trip {index + 1}</Text>
																	<ActionIcon
																		variant="subtle"
																		color="red"
																		onClick={() =>
																			removeTransportationEntry(index)
																		}
																	>
																		<IconTrash size={16} />
																	</ActionIcon>
																</Group>

																<Grid>
																	<Grid.Col span={6}>
																		<Stack gap="xs">
																			<Text fw={500}>Start Time</Text>
																			<Input
																				type="time"
																				value={entry.startTime}
																				onChange={(e) =>
																					updateTransportationEntry(index, {
																						startTime: e.target.value,
																					})
																				}
																			/>
																			{timeWarnings.startBeforeScheduled?.[
																				index
																			] && (
																				<Text size="sm" c="yellow">
																					Start time is before scheduled shift
																					start
																				</Text>
																			)}
																		</Stack>
																	</Grid.Col>

																	<Grid.Col span={6}>
																		<Stack gap="xs">
																			<Text fw={500}>End Time</Text>
																			<Input
																				type="time"
																				value={entry.endTime}
																				onChange={(e) =>
																					updateTransportationEntry(index, {
																						endTime: e.target.value,
																					})
																				}
																			/>
																			{timeWarnings.endAfterScheduled?.[
																				index
																			] && (
																				<Text size="sm" c="yellow">
																					End time is after scheduled shift end
																				</Text>
																			)}
																		</Stack>
																	</Grid.Col>
																</Grid>

																{timeWarnings.startAfterEnd?.[index] && (
																	<Text size="sm" c="red">
																		Start time cannot be after end time
																	</Text>
																)}

																<Stack gap="xs">
																	<Text fw={500}>Total Miles</Text>
																	<NumberInput
																		min={0}
																		value={
																			typeof entry.totalMiles === "number"
																				? entry.totalMiles
																				: ""
																		}
																		onChange={(value) =>
																			updateTransportationEntry(index, {
																				totalMiles: value
																					? Number(value)
																					: undefined,
																			})
																		}
																	/>
																</Stack>

																<Stack gap="xs">
																	<Text fw={500}>Destination and Purpose</Text>
																	<Textarea
																		value={entry.destinationAndPurpose}
																		onChange={(e) =>
																			updateTransportationEntry(index, {
																				destinationAndPurpose: e.target.value,
																			})
																		}
																		maxLength={255}
																		minLength={1}
																	/>
																</Stack>

																<Grid>
																	<Grid.Col span={6}>
																		<Stack gap="xs">
																			<Text fw={500}>
																				Errands Time (minutes)
																			</Text>
																			<NumberInput
																				min={0}
																				value={
																					typeof entry.errandsTime === "number"
																						? entry.errandsTime
																						: ""
																				}
																				onChange={(value) =>
																					updateTransportationEntry(index, {
																						errandsTime: value
																							? Number(value)
																							: undefined,
																					})
																				}
																			/>
																		</Stack>
																	</Grid.Col>

																	<Grid.Col span={6}>
																		<Stack gap="xs">
																			<Text fw={500}>
																				Activities Time (minutes)
																			</Text>
																			<NumberInput
																				min={0}
																				value={
																					typeof entry.activitiesTime ===
																					"number"
																						? entry.activitiesTime
																						: ""
																				}
																				onChange={(value) =>
																					updateTransportationEntry(index, {
																						activitiesTime: value
																							? Number(value)
																							: undefined,
																					})
																				}
																			/>
																		</Stack>
																	</Grid.Col>
																</Grid>

																<Stack gap="xs">
																	<Text fw={500}>Was client with you?</Text>
																	<Radio.Group
																		value={
																			entry.wasClientWithYou?.toString() ?? ""
																		}
																		onChange={(value) =>
																			updateTransportationEntry(index, {
																				wasClientWithYou: value === "true",
																			})
																		}
																	>
																		<Group>
																			<Radio value="true" label="Yes" />
																			<Radio value="false" label="No" />
																		</Group>
																	</Radio.Group>
																</Stack>
															</Stack>
														</Card>
													))}

													{transportationData.length <
														maxTransportationEntries && (
														<Button
															onClick={addTransportationEntry}
															variant="outline"
															leftSection={<IconPlus size={16} />}
															fullWidth
														>
															Add More
														</Button>
													)}
												</Stack>
											)}
										</Stack>
									</Card>
								</Stack>
							</Box>
						</Box>
					</Flex>

					<Box
						style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
						p="sm"
					>
						<Box style={{ maxWidth: "800px", margin: "0 auto", width: "100%" }}>
							<Group gap="md" grow>
								<Button variant="outline" onClick={goToPreviousStep}>
									Back
								</Button>
								<Button
									onClick={goToNextStep}
									disabled={!isStepValid("transportation")}
								>
									Next: Comment
								</Button>
							</Group>
						</Box>
					</Box>
				</>
			)}
			{currentStep === "comment" && (
				<>
					<Flex
						direction="column"
						style={{ flex: 1, overflowY: "auto" }}
						ref={innerScrollRef}
					>
						<Box
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								padding: "var(--mantine-spacing-md)",
							}}
						>
							<Box style={{ width: "100%", maxWidth: "800px" }}>
								<Stack gap="md">
									<Card withBorder p="sm">
										<Stack p="sm" gap="md">
											<Title order={4}>
												Caregiver Comment
												{(hasComplianceIssues() || hasFormChanges) && (
													<Text span c="red" ml={4}>
														*
													</Text>
												)}
											</Title>
											{hasComplianceIssues() && (
												<Alert color="red" icon={<IconAlertCircle size={16} />}>
													Comment required due to compliance issues. Please
													explain any exceptions.
												</Alert>
											)}
											{hasFormChanges && !hasComplianceIssues() && (
												<Alert color="red" icon={<IconAlertCircle size={16} />}>
													Comment required when making changes to tasks or
													transportation.
												</Alert>
											)}
											<Textarea
												placeholder="Add any additional comments about this visit"
												value={caregiverComment}
												onChange={(e) => {
													setCaregiverComment(e.target.value);
													// adding/editing a comment does NOT clear signatures
												}}
												maxLength={250}
												required={hasComplianceIssues() || hasFormChanges}
												minRows={3}
												size="md"
											/>
										</Stack>
									</Card>
								</Stack>
							</Box>
						</Box>
					</Flex>

					<Box
						style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
						p="sm"
					>
						<Box style={{ maxWidth: "800px", margin: "0 auto", width: "100%" }}>
							<Group gap="md" grow>
								<Button variant="outline" onClick={goToPreviousStep}>
									Back
								</Button>
								<Button
									onClick={goToNextStep}
									disabled={!isStepValid("comment")}
								>
									Review & Sign
								</Button>
							</Group>
						</Box>
					</Box>
				</>
			)}
			{currentStep === "review" && (
				<>
					<Flex
						direction="column"
						style={{ flex: 1, overflowY: "auto" }}
						ref={innerScrollRef}
					>
						<Box
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								padding: "var(--mantine-spacing-md)",
							}}
						>
							<Box style={{ width: "100%", maxWidth: "800px" }}>
								<Stack gap="md">
									{/* Visit Details */}
									<Card withBorder p="sm">
										<Stack p="sm" gap="md">
											<Title order={4}>Visit Details</Title>
											<Stack gap="md">
												<Grid>
													<Grid.Col span={6}>
														<Stack gap={4}>
															<Text fw={500}>Client</Text>
															<Text size="sm">
																{checkoutDetails.shift.clients.first_name}{" "}
																{checkoutDetails.shift.clients.last_name}
															</Text>
															<Text size="sm">
																{[
																	checkoutDetails.shift.client_addresses
																		?.street1,
																	checkoutDetails.shift.client_addresses
																		?.street2,
																	checkoutDetails.shift.client_addresses?.city,
																	checkoutDetails.shift.client_addresses?.state,
																	checkoutDetails.shift.client_addresses?.zip,
																]
																	.filter(Boolean)
																	.join(" ")}
															</Text>
														</Stack>
													</Grid.Col>
													<Grid.Col span={6}>
														<Stack gap={4}>
															<Text fw={500}>Caregiver</Text>
															<Text size="sm">
																{checkoutDetails.shift.caregivers?.first_name}{" "}
																{checkoutDetails.shift.caregivers?.last_name}
															</Text>
														</Stack>
													</Grid.Col>
												</Grid>

												<Divider />

												<Grid>
													<Grid.Col span={6}>
														<Stack gap={4}>
															<Text fw={500}>Visit Date</Text>
															<Text size="sm">
																<LocalizedTimeText
																	value={checkoutDetails.shift.start_timestamp}
																	format="MMMM D, YYYY"
																/>
															</Text>
														</Stack>
													</Grid.Col>
													<Grid.Col span={6}>
														<Stack gap={4}>
															<Text fw={500}>Scheduled Time</Text>
															<Text size="sm">
																<LocalizedTimeText
																	value={checkoutDetails.shift.start_timestamp}
																	format="h:mm A"
																/>{" "}
																-{" "}
																<LocalizedTimeText
																	value={checkoutDetails.shift.end_timestamp}
																	format="h:mm A"
																/>
															</Text>
														</Stack>
													</Grid.Col>
												</Grid>

												<Stack gap={4}>
													<Text fw={500}>Actual Time</Text>
													<Stack gap={0}>
														<Text>
															Check In:{" "}
															{checkInDateTime ? (
																checkInDateTime.format("MM/DD h:mm A")
															) : (
																<LocalizedTimeText
																	value={
																		checkoutDetails.shift.timesheets?.check_in
																	}
																	format="MM/DD h:mm A"
																/>
															)}
														</Text>
														<Text>
															Check Out:{" "}
															{checkOutDateTime ? (
																checkOutDateTime.format("MM/DD h:mm A")
															) : (
																<LocalizedTimeText
																	value={
																		checkoutDetails.shift.timesheets?.check_out
																	}
																	format="MM/DD h:mm A"
																/>
															)}
														</Text>
													</Stack>
												</Stack>
											</Stack>
										</Stack>
									</Card>

									{/* Completed Tasks */}
									<Card withBorder p="sm">
										<Stack p="sm" gap="md">
											<Title order={4}>Completed Tasks</Title>
											<Stack gap="md">
												{Object.entries(tasksByCategory).map(
													([category, tasks]) => {
														const categoryTasks = tasks.filter((task) =>
															completedTasks.some(
																(t) =>
																	t.category === task.category &&
																	t.name === task.name,
															),
														);
														if (!categoryTasks.length) return null;
														return (
															<Stack key={category} gap="xs">
																<Text
																	fw={500}
																	style={{
																		borderBottom:
																			"1px solid var(--mantine-color-gray-3)",
																		paddingBottom: 4,
																	}}
																>
																	{category}
																</Text>
																<Stack gap="xs">
																	{categoryTasks.map((task, i: number) => {
																		const completedTask = completedTasks.find(
																			(t) =>
																				t.category === task.category &&
																				t.name === task.name,
																		);
																		const previouslyCompletedTask =
																			checkoutDetails.shift.completed_tasks?.find(
																				(t) =>
																					t.category === task.category &&
																					t.name === task.name,
																			);

																		const existingTaskStat =
																			checkoutDetails.taskStats.find(
																				(stat) =>
																					stat.category === task.category &&
																					stat.name === task.name,
																			);

																		const taskStat =
																			completedTask?.simulatedStats ||
																			(previouslyCompletedTask &&
																			existingTaskStat &&
																			!completedTask
																				? {
																						...existingTaskStat,
																						totalOccurrences:
																							existingTaskStat.totalOccurrences -
																							1,
																						totalMinutes:
																							existingTaskStat.totalMinutes -
																							(previouslyCompletedTask?.timeMinutes ||
																								0),
																					}
																				: existingTaskStat);
																		return (
																			<Card
																				key={`${task.category}-${task.name}-${i}`}
																				withBorder
																				p="xs"
																			>
																				<Stack gap={4}>
																					<Group gap="xs">
																						<IconCheck
																							size={16}
																							color="var(--mantine-color-green-6)"
																						/>
																						<Text fw={500} size="sm">
																							{task.name}
																						</Text>
																					</Group>
																					{completedTask?.notes && (
																						<Text size="xs" c="dimmed">
																							<Text span fw={500}>
																								Notes:
																							</Text>{" "}
																							{completedTask.notes}
																						</Text>
																					)}
																					{completedTask?.timeMinutes && (
																						<Text size="xs">
																							<Text span fw={500}>
																								Time Spent Today:
																							</Text>{" "}
																							{completedTask.timeMinutes}{" "}
																							minutes
																						</Text>
																					)}
																					{taskStat && (
																						<Text c="dimmed">
																							<Text className="font-medium">
																								{"Week to date: "}
																							</Text>
																							{taskStat.totalOccurrences} times,{" "}
																							{taskStat.totalMinutes} minutes
																						</Text>
																					)}
																					{completedTask && (
																						<TaskWarnings
																							task={completedTask}
																						/>
																					)}
																				</Stack>
																			</Card>
																		);
																	})}
																</Stack>
															</Stack>
														);
													},
												)}
											</Stack>
										</Stack>
									</Card>

									{/* Missing Required Tasks Warning */}
									<MissingRequiredTasksWarning
										checkoutDetails={checkoutDetails}
										completedTasks={completedTasks}
									/>

									{/* Transportation Summary */}
									{includeTransportation && transportationData.length > 0 && (
										<Card withBorder p="sm">
											<Stack p="sm" gap="md">
												<Title order={4}>Transportation Summary</Title>
												{!checkoutDetails.transportationAllowed && (
													<Alert
														color="yellow"
														icon={<IconAlertTriangle size={16} />}
													>
														Transportation is not allowed in the care plan
													</Alert>
												)}
												<Stack gap="lg">
													{transportationData.map((entry, index) => (
														<Card
															key={`transportation-${entry.startTime}-${entry.endTime}-${index}`}
															withBorder
															p="sm"
														>
															<Stack gap="md">
																<Title order={5}>Trip {index + 1}</Title>
																<Grid>
																	<Grid.Col span={6}>
																		<Stack gap={4}>
																			<Text fw={500}>Time</Text>
																			<Text size="sm">
																				{format24HourTime(entry.startTime)} -{" "}
																				{format24HourTime(entry.endTime)}
																			</Text>
																		</Stack>
																	</Grid.Col>
																	<Grid.Col span={6}>
																		<Stack gap={4}>
																			<Text fw={500}>Distance</Text>
																			<Text size="sm">
																				{entry.totalMiles} miles
																			</Text>
																		</Stack>
																	</Grid.Col>
																	<Grid.Col span={12}>
																		<Stack gap={4}>
																			<Text fw={500}>Purpose</Text>
																			<Text size="sm" c="dimmed">
																				{entry.destinationAndPurpose}
																			</Text>
																		</Stack>
																	</Grid.Col>
																	<Grid.Col span={6}>
																		<Stack gap={4}>
																			<Text fw={500}>Time Breakdown</Text>
																			<Stack gap={0}>
																				<Text>
																					Errands: {entry.errandsTime} minutes
																				</Text>
																				<Text>
																					Activities: {entry.activitiesTime}{" "}
																					minutes
																				</Text>
																			</Stack>
																		</Stack>
																	</Grid.Col>
																	<Grid.Col span={6}>
																		<Stack gap={4}>
																			<Text fw={500}>Client Present</Text>
																			<Text size="sm">
																				{entry.wasClientWithYou ? "Yes" : "No"}
																			</Text>
																		</Stack>
																	</Grid.Col>
																</Grid>
															</Stack>
														</Card>
													))}
												</Stack>
											</Stack>
										</Card>
									)}

									{caregiverComment && (
										<Card withBorder p="sm">
											<Stack p="sm" gap="md">
												<Title order={4}>Caregiver Comment</Title>
												<Text size="sm">{caregiverComment}</Text>
											</Stack>
										</Card>
									)}

									{/* Client verification */}
									{clientVerificationRequired && (
										<Card
											withBorder={clientVerificationMethodCount > 1}
											p={clientVerificationMethodCount > 1 ? "sm" : 0}
										>
											<Stack
												p={clientVerificationMethodCount > 1 ? "sm" : 0}
												gap="md"
											>
												{clientVerificationMethodCount > 1 && (
													<Title order={4}>Client Verification</Title>
												)}
												{clientVerificationMethodCount > 1 && (
													<Radio.Group
														label="Choose one verification method"
														value={clientVerificationMethod || ""}
														onChange={(value) =>
															selectClientVerificationMethod(
																value as ClientVerificationMethod,
															)
														}
													>
														<Stack gap="xs" mt="xs">
															{clientVoiceVerificationEnabled && (
																<Radio
																	value="voiceRecording"
																	label="Voice confirmation"
																/>
															)}
															{clientSignatureVerificationEnabled && (
																<Radio
																	value="signature"
																	label="Client signature"
																/>
															)}
															{clientUnableToVerifyEnabled && (
																<Radio
																	value="unableToVerifyReason"
																	label="Client unable to verify"
																/>
															)}
														</Stack>
													</Radio.Group>
												)}

												{clientVerificationMethod === "voiceRecording" && (
													<Stack gap="md">
														<Title order={5}>Client Voice Confirmation</Title>
														{savedClientVerbalConfirmationRecording ? (
															<Text size="sm">
																Voice confirmation previously captured
															</Text>
														) : (
															<VerbalConfirmationInput
																value={clientVerbalConfirmationRecording}
																clientName={`${checkoutDetails.shift.clients.first_name} ${checkoutDetails.shift.clients.last_name}`}
																shiftId={shiftId}
																disclaimer={checkoutDetails.signatureDisclaimer}
																onRecorded={(result) => {
																	setClientVerbalConfirmationRecording(result);
																	setClientUnableToSignReason(null);
																	setClientInitials(null);
																	setClientSignature(null);
																	setSavedClientVerbalConfirmationRecording(
																		null,
																	);
																	setClientVerificationMethod("voiceRecording");
																}}
															/>
														)}
													</Stack>
												)}
												<>
													{checkoutDetails.clientInitialsRequired &&
														clientVerificationMethod === "signature" && (
															<Card withBorder p="sm">
																<Stack p="sm" gap="md">
																	<Title order={4}>Client Initials</Title>

																	<>
																		<Text size="sm" c="dimmed">
																			{[
																				caregiverAppSignatureDisclaimer,
																				checkoutDetails.signatureDisclaimer,
																			]
																				.filter(Boolean)
																				.join("\n")}
																		</Text>
																		<SignatureInput
																			value={clientInitials}
																			onChange={(value) => {
																				setClientInitials(value);
																				if (value) {
																					setClientUnableToSignReason(null);
																					setClientVerbalConfirmationRecording(
																						null,
																					);
																					setSavedClientVerbalConfirmationRecording(
																						null,
																					);
																					setClientVerificationMethod(
																						"signature",
																					);
																				}
																			}}
																			dialogDescription={
																				caregiverAppClientSignatureDialogDescription
																			}
																			signerName={`${checkoutDetails.shift.clients.first_name} ${checkoutDetails.shift.clients.last_name}`}
																			width={250}
																		>
																			<Stack gap={0} align="flex-end">
																				{!checkoutDetails.hasSubmittedSurveyInThePast90Days &&
																					isRandomSurveyEnabled && (
																						<Button
																							variant="transparent"
																							onClick={() => {
																								toggleSurvey();
																							}}
																							m={0}
																							p={0}
																						>
																							{!isSurveyHidden
																								? "Hide Survey"
																								: "Show Survey"}
																						</Button>
																					)}

																				{Boolean(
																					!checkoutDetails.hasSubmittedSurveyInThePast90Days &&
																						isRandomSurveyEnabled &&
																						!isSurveyHidden,
																				) && (
																					<Card withBorder p="sm">
																						<Text
																							size="md"
																							fw={500}
																							style={{ textWrap: "pretty" }}
																						>
																							How likely are you to recommend
																							our caregiving services to friends
																							or family?
																						</Text>

																						<Group
																							justify="space-between"
																							mt="sm"
																							mb="xs"
																						>
																							<Text size="xs" c="dimmed">
																								Not likely at all
																							</Text>
																							<Text size="xs" c="dimmed">
																								Extremely likely
																							</Text>
																						</Group>

																						<SimpleGrid
																							cols={isMobile ? 5 : 10}
																							spacing={isMobile ? "xs" : "sm"}
																							verticalSpacing={
																								isMobile ? "xs" : "sm"
																							}
																						>
																							{[
																								1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
																							].map((score) => (
																								<Button
																									key={score}
																									variant={
																										experienceRating === score
																											? "filled"
																											: "default"
																									}
																									color={getExperienceRatingButtonColor(
																										score,
																									)}
																									onClick={() =>
																										setExperienceRating(
																											experienceRating === score
																												? null
																												: score,
																										)
																									}
																									style={{
																										height: isMobile
																											? "2.5rem"
																											: "3rem",
																										minWidth: isMobile
																											? "2.5rem"
																											: "auto",
																										padding: isMobile
																											? "0"
																											: undefined,
																									}}
																								>
																									{score}
																								</Button>
																							))}
																						</SimpleGrid>
																					</Card>
																				)}
																			</Stack>
																		</SignatureInput>

																		{experienceRating && (
																			<Text size="sm" ta="center">
																				Thank you for rating your experience.
																			</Text>
																		)}
																	</>
																</Stack>
															</Card>
														)}

													{checkoutDetails.clientSignatureRequired &&
														clientVerificationMethod === "signature" && (
															<Card withBorder p="sm">
																<Stack p="sm" gap="md">
																	<Title order={4}>Client Signature</Title>

																	<>
																		<Text size="sm" c="dimmed">
																			{[
																				caregiverAppSignatureDisclaimer,
																				checkoutDetails.signatureDisclaimer,
																			]
																				.filter(Boolean)
																				.join("\n")}
																		</Text>
																		<SignatureInput
																			value={clientSignature}
																			onChange={(value) => {
																				setClientSignature(value);
																				if (value) {
																					setClientUnableToSignReason(null);
																					setClientVerbalConfirmationRecording(
																						null,
																					);
																					setSavedClientVerbalConfirmationRecording(
																						null,
																					);
																					setClientVerificationMethod(
																						"signature",
																					);
																				}
																			}}
																			dialogDescription={
																				caregiverAppClientSignatureDialogDescription
																			}
																			signerName={`${checkoutDetails.shift.clients.first_name} ${checkoutDetails.shift.clients.last_name}`}
																		>
																			<Stack gap={0} align="flex-end">
																				{!checkoutDetails.hasSubmittedSurveyInThePast90Days &&
																					isRandomSurveyEnabled && (
																						<Button
																							variant="transparent"
																							onClick={() => {
																								toggleSurvey();
																							}}
																							m={0}
																							p={0}
																						>
																							{!isSurveyHidden
																								? "Hide Survey"
																								: "Show Survey"}
																						</Button>
																					)}
																				{!checkoutDetails.hasSubmittedSurveyInThePast90Days &&
																					isRandomSurveyEnabled &&
																					!isSurveyHidden && (
																						<Card withBorder p="sm">
																							<Text
																								size="md"
																								fw={500}
																								style={{ textWrap: "pretty" }}
																							>
																								How likely are you to recommend
																								our caregiving services to
																								friends or family?
																							</Text>

																							<Group
																								justify="space-between"
																								mt="sm"
																								mb="xs"
																							>
																								<Text size="xs" c="dimmed">
																									Not likely at all
																								</Text>
																								<Text size="xs" c="dimmed">
																									Extremely likely
																								</Text>
																							</Group>

																							<SimpleGrid
																								cols={isMobile ? 5 : 10}
																								spacing={isMobile ? "xs" : "sm"}
																								verticalSpacing={
																									isMobile ? "xs" : "sm"
																								}
																							>
																								{[
																									1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
																								].map((score) => (
																									<Button
																										key={score}
																										variant={
																											experienceRating === score
																												? "filled"
																												: "default"
																										}
																										color={getExperienceRatingButtonColor(
																											score,
																										)}
																										onClick={() =>
																											setExperienceRating(
																												experienceRating ===
																													score
																													? null
																													: score,
																											)
																										}
																										style={{
																											height: isMobile
																												? "2.5rem"
																												: "3rem",
																											minWidth: isMobile
																												? "2.5rem"
																												: "auto",
																											padding: isMobile
																												? "0"
																												: undefined,
																										}}
																									>
																										{score}
																									</Button>
																								))}
																							</SimpleGrid>
																						</Card>
																					)}
																			</Stack>
																		</SignatureInput>

																		{experienceRating && (
																			<Text size="sm" ta="center">
																				Thank you for rating your experience.
																			</Text>
																		)}
																	</>
																</Stack>
															</Card>
														)}
												</>

												{(checkoutDetails.clientInitialsRequired ||
													checkoutDetails.clientSignatureRequired) &&
													clientVerificationMethod ===
														"unableToVerifyReason" && (
														<Card withBorder p="sm">
															<Stack p="sm" gap="md">
																<Title order={4}>Client Unable to Verify</Title>
																{clientUnableToSignReason ? (
																	<Stack gap="xs">
																		<Text size="sm" c="dimmed">
																			Reason: {clientUnableToSignReason}
																		</Text>
																		<Button
																			variant="outline"
																			size="xs"
																			onClick={() => {
																				setUnableToSignModalOpened(true);
																			}}
																		>
																			Change Reason
																		</Button>
																	</Stack>
																) : (
																	<Button
																		variant="outline"
																		size="xs"
																		onClick={() =>
																			setUnableToSignModalOpened(true)
																		}
																	>
																		Select Reason
																	</Button>
																)}
															</Stack>
														</Card>
													)}
											</Stack>
										</Card>
									)}
									{checkoutDetails.caregiverSignatureRequired && (
										<Card withBorder p="sm">
											<Stack p="sm" gap="md">
												<Title order={4}>Caregiver Signature</Title>
												<Text size="sm" c="dimmed">
													{[
														caregiverAppSignatureDisclaimer,
														checkoutDetails.signatureDisclaimer,
													]
														.filter(Boolean)
														.join("\n")}
												</Text>
												<SignatureInput
													value={caregiverSignature}
													onChange={(value) => setCaregiverSignature(value)}
													signerName={`${checkoutDetails.shift.caregivers?.first_name} ${checkoutDetails.shift.caregivers?.last_name}`}
													dialogDescription={caregiverAppSignatureDisclaimer}
												/>
											</Stack>
										</Card>
									)}

									{checkoutDetails.caregiverInitialsRequired && (
										<Card withBorder p="sm">
											<Stack p="sm" gap="md">
												<Title order={4}>Caregiver Initials</Title>
												<Text size="sm" c="dimmed">
													{[
														caregiverAppSignatureDisclaimer,
														checkoutDetails.signatureDisclaimer,
													]
														.filter(Boolean)
														.join("\n")}
												</Text>
												<SignatureInput
													value={caregiverInitials}
													onChange={(value) => setCaregiverInitials(value)}
													signerName={`${checkoutDetails.shift.caregivers?.first_name} ${checkoutDetails.shift.caregivers?.last_name}`}
													width={250}
													dialogDescription={caregiverAppSignatureDisclaimer}
												/>
											</Stack>
										</Card>
									)}
								</Stack>
							</Box>
						</Box>
					</Flex>

					<Box
						style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
						p="sm"
					>
						<Box style={{ maxWidth: "800px", margin: "0 auto", width: "100%" }}>
							<Group gap="md" grow>
								<Button variant="outline" onClick={goToPreviousStep}>
									Back to Edit
								</Button>
								<Button
									onClick={handleSubmit}
									disabled={Boolean(
										localSaveCareSheetCheckOut.isPending ||
											checkOutDetailInitialLoading ||
											verificationIncomplete,
									)}
									loading={localSaveCareSheetCheckOut.isPending}
								>
									Submit
								</Button>
							</Group>
						</Box>
					</Box>
				</>
			)}
			<Modal
				opened={unableToSignModalOpened}
				onClose={() => setUnableToSignModalOpened(false)}
				title="Unable to Verify Reason"
				size="md"
				centered
			>
				<Stack gap="md">
					<Text size="sm" c="orange" fw={500}>
						Client verification is required unless the client is unable to
						verify for the following reasons and ONLY the following reasons:
					</Text>
					<Select
						label="Reason"
						placeholder="Select a reason"
						data={CLIENT_UNABLE_TO_SIGN_REASONS}
						value={clientUnableToSignReason}
						onChange={(value) => {
							setClientUnableToSignReason(value);
							if (value) {
								setClientSignature(null);
								setClientInitials(null);
								setClientVerbalConfirmationRecording(null);
								setSavedClientVerbalConfirmationRecording(null);
								setClientVerificationMethod("unableToVerifyReason");
							}
						}}
						allowDeselect
					/>
					<Group justify="flex-end" gap="xs">
						<Button
							variant="outline"
							onClick={() => {
								setUnableToSignModalOpened(false);
								clearClientVerification();
							}}
						>
							Clear
						</Button>
						<Button
							onClick={() => setUnableToSignModalOpened(false)}
							disabled={!clientUnableToSignReason}
						>
							Confirm
						</Button>
					</Group>
				</Stack>
			</Modal>
			<Modal
				opened={timeDifferenceModalOpened}
				onClose={() => setTimeDifferenceModalOpened(false)}
				title="Confirm Time Entry"
				size="md"
				centered
			>
				<Stack gap="md">
					<Text size="sm">
						The time entered is significantly different from the scheduled time.
						Are you sure you want to continue?
					</Text>
					{checkInSignificantlyDifferent &&
						checkInDateTime &&
						checkInDiff !== null && (
							<TimeDifferenceDetail
								label="Check In"
								actual={checkInDateTime}
								scheduled={dayjs
									.utc(checkoutDetails.shift.start_timestamp)
									.local()}
								diffMinutes={checkInDiff}
							/>
						)}
					{checkOutSignificantlyDifferent &&
						checkOutDateTime &&
						checkOutDiff !== null && (
							<TimeDifferenceDetail
								label="Check Out"
								actual={checkOutDateTime}
								scheduled={dayjs
									.utc(checkoutDetails.shift.end_timestamp)
									.local()}
								diffMinutes={checkOutDiff}
							/>
						)}
					<Group justify="flex-end" gap="xs">
						<Button
							variant="outline"
							onClick={() => setTimeDifferenceModalOpened(false)}
						>
							Back to edit
						</Button>
						<Button
							onClick={() => {
								setTimeDifferenceModalOpened(false);
								proceedFromTimeEntryStep();
							}}
						>
							It is correct
						</Button>
					</Group>
				</Stack>
			</Modal>
		</Flex>
	);
}

function TimeDifferenceDetail({
	label,
	actual,
	scheduled,
	diffMinutes,
}: {
	label: string;
	actual: dayjs.Dayjs;
	scheduled: dayjs.Dayjs;
	diffMinutes: number;
}) {
	const showTimeDiffHint = shouldShowTimeDiffHint(diffMinutes);

	// Alert's colored background/border gives this enough contrast to read in light
	// mode, unlike plain colored text. Escalate to red when we have a specific hint
	// (likely AM/PM or date mistake) since that's more actionable than a plain warning.
	return (
		<Alert
			color={showTimeDiffHint ? "red" : "yellow"}
			icon={<IconAlertTriangle size={16} />}
			title={label}
		>
			<Stack gap={4}>
				<Text size="sm">Entered: {actual.format("MM/DD h:mm A")}</Text>
				<Text size="sm">Scheduled: {scheduled.format("MM/DD h:mm A")}</Text>
				<Text size="sm" fw={500}>
					Difference: {formatMinutesDuration(diffMinutes)}
				</Text>
				{showTimeDiffHint && (
					<Text size="sm" fw={500}>
						This looks like it may be off by a half day or more. Double check
						that the date and AM/PM are both correct.
					</Text>
				)}
			</Stack>
		</Alert>
	);
}

function TaskWarnings({ task }: { task: TaskWithWarnings }) {
	if (!task.simulatedStats) return null;

	return (
		<Stack gap="xs">
			{task.simulatedStats.isWrongDay && (
				<Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
					Task scheduled for{" "}
					{task.simulatedStats.allowedDays
						?.map(
							(day) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day],
						)
						.join(", ")}
				</Alert>
			)}
			{task.simulatedStats.isOverOccurrences && (
				<Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
					{task.simulatedStats.occurrencesFrequency === "perDay"
						? "Exceeds daily occurrence limit"
						: `Exceeds weekly limit of ${task.simulatedStats.occurrencesLimit} times`}
				</Alert>
			)}
			{task.simulatedStats.isOverMinutes && (
				<Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
					{task.simulatedStats.minutesFrequency === "perDay"
						? "Exceeds daily time limit"
						: `Exceeds weekly time limit of ${task.simulatedStats.minutesLimit} minutes`}
				</Alert>
			)}
		</Stack>
	);
}

function MissingRequiredTasksWarning({
	checkoutDetails,
	completedTasks,
}: {
	checkoutDetails: {
		shift: {
			start_timestamp: string;
		};
		taskStats: {
			category: string;
			name: string;
			allowedDays?: number[] | null | undefined;
			occurrencesFrequency?: "perDay" | "perWeek" | null | undefined;
			minutesLimit?: number | null | undefined;
			minutesFrequency?: "perDay" | "perWeek" | null | undefined;
			occurrencesLimit?: number | null | undefined;
		}[];
		assignedTasks?: {
			category: string;
			name: string;
			noteRequired?: boolean;
			timeRequired?: boolean;
		}[];
	};
	completedTasks: TaskWithWarnings[];
}) {
	// Get current day of the week (0 = Sunday, 1 = Monday, etc.)
	const currentDayOfWeek = dayjs(checkoutDetails.shift.start_timestamp).day();

	// Find missing required tasks for today
	const missingRequiredTasks = (checkoutDetails.assignedTasks ?? []).filter(
		(task) => {
			// Check if task has specific days configured
			const taskStat = checkoutDetails.taskStats.find(
				(stat) => stat.category === task.category && stat.name === task.name,
			);

			// Determine if task is required for today based on frequency/allowed days
			let isRequiredForToday = false;

			// If task has allowed days, check if today is one of those days
			if (taskStat?.allowedDays?.length) {
				isRequiredForToday = taskStat.allowedDays.includes(currentDayOfWeek);
			} else if (
				taskStat?.occurrencesFrequency === "perDay" &&
				!taskStat?.allowedDays?.length &&
				taskStat?.occurrencesLimit !== undefined &&
				taskStat?.occurrencesLimit !== null &&
				taskStat?.occurrencesLimit > 0
			) {
				// If task frequency is daily, it's required every day
				isRequiredForToday = true;
			}

			// If not required for today, skip
			if (!isRequiredForToday) {
				return false;
			}

			// Check if the task was completed
			const isCompleted = completedTasks.some(
				(t) => t.category === task.category && t.name === task.name,
			);

			// Return true if task is required today but not completed
			return !isCompleted;
		},
	);

	if (missingRequiredTasks.length === 0) return null;

	return (
		<Alert color="red" icon={<IconAlertTriangle size={16} />}>
			<Stack gap="xs">
				<Text fw={500}>Scheduled tasks not completed:</Text>
				{missingRequiredTasks.map((task, index: number) => (
					<Text size="sm" key={`${task.category}-${task.name}-${index}`}>
						• {task.category}: {task.name}
					</Text>
				))}
			</Stack>
		</Alert>
	);
}
